import React, { useState } from "react";
import { Box, Modal, Typography, Button } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
    setSelectedSeasonsList,
    setSelectedItems,
    handleComplianceOpen,
    setIsSeries,
} from "../../redux/slices/contentSlice/contentForm/contentForm";
import FilePreview from "./FilePreview";
import { generateClient } from "aws-amplify/api";
import { uploadData } from "aws-amplify/storage";
import { createContent } from "../../graphql/mutations";
import { type } from "@testing-library/user-event/dist/type";
import LicensingRegion from "./LicensingRegion";
import LicensingTerm from "./LicensingTerm";
import Rights from "./Rights";
import { duration } from "moment";
import IsSeries from "./SeriesChecker";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { setLoading } from "../../redux/slices/apiSlice";
import { convertToIso6391 } from "../../helper/convertToIso";
import OneDrivePicker from "./OneDrivePicker2";
import OneDriveFiles from "./OneDriveFiles";
import UploadMultipleFiles from "./UploadMultipleFiles";
import EpisodesUploader from "./EpisodesUploader";
import ComplianceModal from "./ComplianceModal";
import { post } from "aws-amplify/api";
import { WATCH, LISTEN, READ, PLAY, DEVOTION, VODCAST, DREAMKIDS } from "../../constants";

const UploadEpisodes = ({ seasonId }) => {
    const client = generateClient();
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const csvBatchData = useSelector((state) => state.contentForm.csvBatchData);
    const complianceChecked = useSelector(
        (state) => state.contentForm.complianceChecked
    );
    const complianceOpen = useSelector(
        (state) => state.contentForm.complianceOpen
    );
    const agreementForm = useSelector((state) => state.agreementForm);

    const seriesID = useSelector((state) => state.contentForm.seriesID);
    const loading = useSelector((state) => state.api.loading);
    const [uploading, setUploading] = useState(false);
    const [currentBatch, setCurrentBatch] = useState(null);
    const [uploadProgress, setUploadProgress] = useState([]);

    const selectedItems = useSelector(
        (state) => state.contentForm.selectedItems
    );
    const [masterFileContent, setMasterFileContent] = React.useState({
        filePath: "",
        file: "",
    });
    const [posterFileContent, setPosterFileContent] = useState({
        filePath: "",
        file: "",
    });
    const contentForm = useSelector((state) => state.contentForm);
    const dispatch = useDispatch();

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };

    const handleFilePath = (file) => {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        let previewUrl = "";
        let fileType = "";
        const guid = uuidv4();

        let filePath;
        const type = contentForm.contentType;
        const category = contentForm.category;
        let title = "";
        if (csvBatchData && csvBatchData.length > 0) {
            title = csvBatchData[selectedItems?.length].Title;
        }

        filePath = `${type.replace(/\s+/g, "")}/${category.replace(
            /\s+/g,
            ""
        )}/${title.replace(/\s+/g, "")}/${guid}.${fileExtension}`;

        return { filePath, fileType };
    };

    const handleFileChange = (event) => {
        const uploadedFile = event.target.files[0];

        if (uploadedFile) {
            setFile(uploadedFile);
            const { filePath, fileType } = handleFilePath(uploadedFile);
            if (uploadedFile.type.startsWith("image/")) {
                setPosterFileContent({
                    file: uploadedFile,
                    filePath: filePath,
                });
            } else {
                setMasterFileContent({
                    file: uploadedFile,
                    filePath,
                });
            }
        }
    };

    async function blobUrlToFile(blobUrl, fileName) {
        // Fetch the blob data from the blob URL
        const response = await fetch(blobUrl);
        const blob = await response.blob();

        // Create a File object from the blob
        const file = new File([blob], fileName, {
            type: blob.type,
            lastModified: Date.now(),
        });

        return file;
    }

    const postOneDriveContent = async (items) => {
        try {
            // Convert items to the format expected by the API
            const oneDriveItems = items.map(item => ({
                filePath: item.filePath,
                downloadUrl: item.masterFile
            }));
            
            const restOperation = post({
                apiName: "restapi123",
                path: "/onedriveuploader",
                options: {
                    body: {
                        oneDriveItems: oneDriveItems
                    },
                },
            });

            const { body } = await restOperation.response;
            const response = await body.json();
            console.log("OneDrive upload response:", response);
            return true;
        } catch (err) {
            console.error("Error in postOneDriveContent:", err);
            return false;
        }
    };

    const uploadMasterFile = async (item) => {
        try {
            const filePath = item.filePath;
            const uuid = item.masterFile.split("/").pop();
            console.log(`Uploading ${uuid} to ${filePath}`);
            const response = await blobUrlToFile(item.masterFile, uuid);
            
            // Use the uploadData API with progress tracking
            const uploadTask = uploadData({
                key: filePath,
                data: response,
                options: {
                    onProgress: (progress) => {
                        console.log(`Upload progress for ${filePath}: ${progress.loaded}/${progress.total}`);
                        // You could update a progress state here if needed
                    }
                }
            });
            
            await uploadTask.result;
            console.log(`Successfully uploaded ${filePath}`);
            return true;
        } catch (error) {
            console.error(`Error uploading master file ${item.filePath}:`, error);
            return false;
        }
    };

    const uploadContentData = async (item) => {
        try {
            if (item && item.posterFile) {
                const uuid = item.posterFile.split("/").pop();
                const response = await blobUrlToFile(item.posterFile, uuid);
                
                await uploadData({
                    key: item.posterFilePath,
                    data: response
                }).result;
                
                return true;
            }
            return false;
        } catch (error) {
            console.error(`Error uploading poster file ${item.posterFilePath}:`, error);
            return false;
        }
    };

    const handleSubmitSeason = async () => {
        if (!complianceChecked) {
            dispatch(handleComplianceOpen(true));
            return;
        }
        
        if (selectedItems.length === 0) {
            toast.error("No items selected for upload");
            return;
        }
        
        // Make sure we have CSV data for all items
        if (!csvBatchData || csvBatchData?.title?.length !== selectedItems.length) {
            toast.error("CSV data doesn't match selected items");
            return;
        }
        
        setUploading(true);
        dispatch(setLoading(true));
        
        // Initialize progress tracking
        setUploadProgress(selectedItems.map(() => ({ status: 'pending', progress: 0 })));
        
        // Process in smaller batches for better reliability
        const BATCH_SIZE = 2;
        const batches = [];
        
        for (let i = 0; i < selectedItems.length; i += BATCH_SIZE) {
            batches.push(selectedItems.slice(i, i + BATCH_SIZE));
        }
        
        toast.info(`Processing ${selectedItems.length} items in ${batches.length} batches`);
        
        let successCount = 0;
        let failureCount = 0;
        let totalOnedriveItems = [];
        
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
            const batch = batches[batchIndex];
            setCurrentBatch({
                current: batchIndex + 1,
                total: batches.length,
                size: batch.length
            });
            
            toast.info(`Processing batch ${batchIndex + 1} of ${batches.length} (${batch.length} items)`);
            
            // Process items in each batch in parallel
            const results = await Promise.allSettled(
                batch.map(async (item, itemIndex) => {
                    const globalIndex = batchIndex * BATCH_SIZE + itemIndex;
                    
                    try {
                        // Update status to 'uploading'
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[globalIndex] = { status: 'uploading', progress: 10 };
                            return newProgress;
                        });
                        
                        // Upload content data first (poster files)
                        let posterSuccess = await uploadContentData(item);
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[globalIndex] = { status: 'uploading', progress: 30 };
                            return newProgress;
                        });
                        
                        // Prepare content object
                        const content = {
                            title: csvBatchData?.title?.[globalIndex]?.value,
                            synopsis: csvBatchData?.description?.[globalIndex]?.value,
                            seasonId: seasonId,
                            agreementId: agreementForm?.id,
                            status: "Pending",
                            seriesId: seriesID,
                            type: contentForm.contentType,
                            category: contentForm.category,
                            director: csvBatchData?.director?.[globalIndex]?.value,
                            actors: csvBatchData?.actors?.[globalIndex]?.value
                                ?.split(",")
                                .map((item) => item.trim()),
                            duration: csvBatchData?.duration?.[globalIndex]?.value,
                            language: convertToIso6391(
                                csvBatchData?.language?.[globalIndex]?.value
                            ),
                            sequence: globalIndex + 1,
                            filePath: item.filePath,
                            trailerFilePath: "",
                            imdbRating: csvBatchData?.imdbRating?.[globalIndex]?.value,
                            rottenRating:
                                csvBatchData?.rottenRating?.[globalIndex]?.value,
                            rating: csvBatchData?.rating?.[globalIndex]?.value,
                            year: csvBatchData?.year?.[globalIndex]?.value,
                            posters: [item.posterFilePath],
                            genre: csvBatchData?.genre?.[globalIndex]?.value
                                ?.split(",")
                                .map((item) => item.trim()),
                            isSeries: false,
                            isDrmProtected: false,
                            isOneDrive: item.typeOfFile === "onedrive",
                            isGrouped: true,
                        };
                        
                        // Add category-specific fields
                        if (contentForm.category === VODCAST) {
                            if (csvBatchData?.host?.[globalIndex]?.value) {
                                content.host = csvBatchData.host[globalIndex].value
                                    .split(",")
                                    .map(item => item.trim());
                            }
                            
                            if (csvBatchData?.guests?.[globalIndex]?.value) {
                                content.guests = csvBatchData.guests[globalIndex].value
                                    .split(",")
                                    .map(item => item.trim());
                            }
                        } else if (contentForm.category === DREAMKIDS) {
                            if (csvBatchData?.educationalValue?.[globalIndex]?.value) {
                                content.educationalValue = csvBatchData.educationalValue[globalIndex].value;
                            }
                            
                            if (csvBatchData?.ageGroup?.[globalIndex]?.value) {
                                content.ageGroup = csvBatchData.ageGroup[globalIndex].value;
                            }
                        }

                        // Handle file upload based on type
                        if (item.typeOfFile === "local") {
                            // Update progress
                            setUploadProgress(prev => {
                                const newProgress = [...prev];
                                newProgress[globalIndex] = { status: 'uploading', progress: 50 };
                                return newProgress;
                            });
                            
                            const uploadSuccess = await uploadMasterFile(item);
                            if (!uploadSuccess) {
                                throw new Error(`Failed to upload master file for ${content.title}`);
                            }
                            
                            // Update progress
                            setUploadProgress(prev => {
                                const newProgress = [...prev];
                                newProgress[globalIndex] = { status: 'uploading', progress: 70 };
                                return newProgress;
                            });
                        } else if (item.typeOfFile === "onedrive") {
                            // For OneDrive items, we'll collect them and process in one batch later
                            totalOnedriveItems.push(item);
                            
                            // Update progress
                            setUploadProgress(prev => {
                                const newProgress = [...prev];
                                newProgress[globalIndex] = { status: 'uploading', progress: 60 };
                                return newProgress;
                            });
                        }
                        
                        // Create content in database
                        const response = await client.graphql({
                            query: createContent,
                            variables: {
                                input: content,
                            },
                        });
                        
                        if (response.data.createContent) {
                            successCount++;
                            
                            // Update progress to success
                            setUploadProgress(prev => {
                                const newProgress = [...prev];
                                newProgress[globalIndex] = { status: 'success', progress: 100 };
                                return newProgress;
                            });
                            
                            return {
                                success: true,
                                title: content.title,
                                contentId: response.data.createContent.id
                            };
                        } else {
                            throw new Error(`Content created but response invalid for ${content.title}`);
                        }
                    } catch (error) {
                        console.error(`Error processing item ${globalIndex}:`, error);
                        
                        // Update progress to failed
                        setUploadProgress(prev => {
                            const newProgress = [...prev];
                            newProgress[globalIndex] = { status: 'failed', progress: 0, error: error.message };
                            return newProgress;
                        });
                        
                        failureCount++;
                        return {
                            success: false,
                            title: csvBatchData?.title?.[globalIndex]?.value,
                            error: error.message
                        };
                    }
                })
            );
            
            // Log batch results
            console.log(`Batch ${batchIndex + 1} results:`, results);
            
            // Pause briefly between batches to avoid overwhelming services
            if (batchIndex < batches.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
        
        // Process OneDrive items in a single batch if any exist
        if (totalOnedriveItems.length > 0) {
            try {
                toast.info(`Uploading ${totalOnedriveItems.length} OneDrive items`);
                const onedriveSuccess = await postOneDriveContent(totalOnedriveItems);
                
                if (!onedriveSuccess) {
                    toast.error(`Failed to process OneDrive items. Please check logs.`);
                } else {
                    toast.success(`Successfully processed OneDrive items`);
                }
            } catch (error) {
                console.error("Error processing OneDrive items:", error);
                toast.error(`Error processing OneDrive items: ${error.message}`);
            }
        }
        
        // Show final status
        if (successCount > 0) {
            toast.success(`Successfully uploaded ${successCount} of ${selectedItems.length} items`);
        }
        
        if (failureCount > 0) {
            toast.error(`Failed to upload ${failureCount} of ${selectedItems.length} items`);
        }
        
        setUploading(false);
        dispatch(setLoading(false));
        setCurrentBatch(null);
    };

    // Get appropriate content-specific text
    const getBatchSubmitText = () => {
        if (uploading) {
            if (currentBatch) {
                return `Uploading batch ${currentBatch.current}/${currentBatch.total}...`;
            }
            return "Uploading...";
        }
        
        if (contentForm.category === VODCAST) {
            return "Submit Vodcast Batch";
        } else if (contentForm.category === DREAMKIDS) {
            return "Submit DreamKids Batch";
        }
        return "Submit Batch";
    };

    return (
        <div>
            <Box display={"flex"} flexWrap={"wrap"} gap={1}>
                <Box flexBasis={"100%"}>
                    <EpisodesUploader />
                </Box>
            </Box>
            
            {uploadProgress.length > 0 && (
                <Box mt={2} mb={2}>
                    <Typography variant="h6">Upload Progress</Typography>
                    {uploadProgress.map((progress, index) => (
                        <Box key={index} display="flex" alignItems="center" mb={1}>
                            <Typography width={200} noWrap>
                                {csvBatchData?.title?.[index]?.value || `Item ${index + 1}`}
                            </Typography>
                            <Box 
                                flex={1} 
                                height={10} 
                                bgcolor="grey.300" 
                                borderRadius={5}
                                mr={2}
                            >
                                <Box 
                                    height="100%" 
                                    width={`${progress.progress}%`} 
                                    bgcolor={
                                        progress.status === 'success' ? 'success.main' : 
                                        progress.status === 'failed' ? 'error.main' : 
                                        'primary.main'
                                    }
                                    borderRadius={5}
                                />
                            </Box>
                            <Typography width={100}>
                                {progress.status === 'success' ? '✓ Complete' : 
                                progress.status === 'failed' ? '✗ Failed' :
                                progress.status === 'uploading' ? `${progress.progress}%` : 
                                'Pending'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            
            {!complianceChecked && (
                <ComplianceModal
                    open={complianceOpen}
                    handleClose={handleClose}
                />
            )}
            
            <LoadingButton
                onClick={handleSubmitSeason}
                disabled={selectedItems.length !== csvBatchData?.title?.length || uploading}
                variant="contained"
                fullWidth
                loading={loading || uploading}
            >
                {getBatchSubmitText()}
            </LoadingButton>
        </div>
    );
};

export default UploadEpisodes;